import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TableModule } from 'primeng/table';
import { CoreApiService } from '../../../core/service/core-api-service';
import { AppService } from '../../../core/service/app.service';
import { properties } from '../../../../environments/properties';
import { FoodOrderDetail } from '../../../model/food-order-detail.model';
import {
  FOOD_ORDER_STATUS_CONFIG,
  FoodOrderStatusConfig,
  FoodOrderStatusEnum,
} from '../../../model/food-order-status.model';

/** Recorrido normal de un pedido; los estados terminales (cancelado/rechazado/expirado) se muestran aparte. */
const ORDER_PROGRESS: FoodOrderStatusEnum[] = [
  FoodOrderStatusEnum.PENDING,
  FoodOrderStatusEnum.CONFIRMED,
  FoodOrderStatusEnum.PREPARING,
  FoodOrderStatusEnum.READY,
  FoodOrderStatusEnum.ASSIGNED,
  FoodOrderStatusEnum.PICKED_UP,
  FoodOrderStatusEnum.DELIVERED,
];

const TERMINAL_STATUSES = new Set<number>([
  FoodOrderStatusEnum.CANCELLED,
  FoodOrderStatusEnum.REJECTED,
  FoodOrderStatusEnum.EXPIRED,
]);

/**
 * El catálogo de estados del viaje de entrega es independiente del de FoodOrderStatusEnum (ids
 * propios) y puede llegar en inglés o español según lo que mande la DB — se resuelve el color
 * por el NOMBRE del estado, con alias en ambos idiomas, reutilizando la paleta ya aprobada.
 */
const TRIP_STATUS_NAME_ALIASES: Record<string, FoodOrderStatusEnum> = {
  pending: FoodOrderStatusEnum.PENDING,
  pendiente: FoodOrderStatusEnum.PENDING,
  confirmed: FoodOrderStatusEnum.CONFIRMED,
  confirmado: FoodOrderStatusEnum.CONFIRMED,
  confirmada: FoodOrderStatusEnum.CONFIRMED,
  preparing: FoodOrderStatusEnum.PREPARING,
  preparando: FoodOrderStatusEnum.PREPARING,
  ready: FoodOrderStatusEnum.READY,
  listo: FoodOrderStatusEnum.READY,
  lista: FoodOrderStatusEnum.READY,
  assigned: FoodOrderStatusEnum.ASSIGNED,
  asignado: FoodOrderStatusEnum.ASSIGNED,
  asignada: FoodOrderStatusEnum.ASSIGNED,
  picked_up: FoodOrderStatusEnum.PICKED_UP,
  recogido: FoodOrderStatusEnum.PICKED_UP,
  recogida: FoodOrderStatusEnum.PICKED_UP,
  delivered: FoodOrderStatusEnum.DELIVERED,
  entregado: FoodOrderStatusEnum.DELIVERED,
  entregada: FoodOrderStatusEnum.DELIVERED,
  cancelled: FoodOrderStatusEnum.CANCELLED,
  canceled: FoodOrderStatusEnum.CANCELLED,
  cancelado: FoodOrderStatusEnum.CANCELLED,
  cancelada: FoodOrderStatusEnum.CANCELLED,
  rejected: FoodOrderStatusEnum.REJECTED,
  rechazado: FoodOrderStatusEnum.REJECTED,
  rechazada: FoodOrderStatusEnum.REJECTED,
  expired: FoodOrderStatusEnum.EXPIRED,
  expirado: FoodOrderStatusEnum.EXPIRED,
  expirada: FoodOrderStatusEnum.EXPIRED,
};

@Component({
  selector: 'app-food-order-detail',
  standalone: true,
  imports: [CommonModule, ButtonModule, TagModule, TableModule],
  templateUrl: './food-order-detail.component.html',
  styleUrl: './food-order-detail.component.css',
})
export class FoodOrderDetailComponent implements OnInit {
  idOrder = '';
  order: FoodOrderDetail | null = null;
  loading = true;

  readonly progressSteps = ORDER_PROGRESS.map((statusId) => FOOD_ORDER_STATUS_CONFIG[statusId]);

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly apiService: CoreApiService,
    private readonly util: AppService,
  ) {}

  ngOnInit(): void {
    this.idOrder = this.route.snapshot.paramMap.get('idOrder') ?? '';
    if (!this.idOrder) {
      this.loading = false;
      return;
    }
    this.loadDetail();
  }

  goBack(): void {
    this.router.navigate(['/admin/food-orders']);
  }

  private loadDetail(): void {
    this.apiService.consumirWSGet(
      properties.serviceGetFoodOrderDetail + '/' + this.idOrder,
      {},
      (res: any) => {
        this.loading = false;
        if (res.estado === properties.codeOK && res.data) {
          this.order = res.data;
        } else {
          this.util.mostrarToast('Pedidos', res.mensaje || 'No se pudo cargar el detalle del pedido', 4);
        }
      },
    );
  }

  getStatusConfig(statusId: number): FoodOrderStatusConfig | undefined {
    return FOOD_ORDER_STATUS_CONFIG[statusId as FoodOrderStatusEnum];
  }

  /** Índice del estado actual dentro del recorrido normal, o -1 si es un estado terminal. */
  get currentStepIndex(): number {
    if (!this.order || TERMINAL_STATUSES.has(this.order.statusId)) {
      return -1;
    }
    return ORDER_PROGRESS.indexOf(this.order.statusId as FoodOrderStatusEnum);
  }

  get isTerminalStatus(): boolean {
    return !!this.order && TERMINAL_STATUSES.has(this.order.statusId);
  }

  /** El pedido llegó al final feliz del recorrido: el último paso también cuenta como completado. */
  get isDelivered(): boolean {
    return !!this.order && this.order.statusId === FoodOrderStatusEnum.DELIVERED;
  }

  /** Fecha de entrega del pedido: la propia si llegó, si no la del viaje de entrega asociado. */
  get orderDeliveredAt(): string | null {
    return this.order?.deliveredAt ?? this.order?.deliveryTrip?.deliveredAt ?? null;
  }

  /** Formato tipo comprobante: 120 -> "000-000-120". */
  formatOrderNumber(orderId: number): string {
    const padded = orderId.toString().padStart(9, '0');
    return `${padded.slice(0, 3)}-${padded.slice(3, 6)}-${padded.slice(6, 9)}`;
  }

  /** Texto legible a partir de un statusName en snake_case (ej. "picked_up" -> "Picked up"). */
  formatStatusName(statusName: string): string {
    const text = statusName.replace(/_/g, ' ');
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  /**
   * Color para el estado del viaje de entrega, buscado por NOMBRE (no por id: el catálogo de
   * estados del viaje usa ids propios, distintos a los de FoodOrderStatusEnum), con alias en
   * inglés y español (ver TRIP_STATUS_NAME_ALIASES). Si no coincide con ningún alias conocido,
   * cae a rojo cuando el texto sugiere cancelación, o a un gris neutro en cualquier otro caso.
   */
  getTripStatusColor(statusName: string): string {
    const normalized = statusName.trim().toLowerCase().replace(/[\s-]+/g, '_');
    const enumValue = TRIP_STATUS_NAME_ALIASES[normalized];
    if (enumValue !== undefined) {
      return FOOD_ORDER_STATUS_CONFIG[enumValue].color;
    }

    if (normalized.includes('cancel')) {
      return FOOD_ORDER_STATUS_CONFIG[FoodOrderStatusEnum.CANCELLED].color;
    }

    return '#5f6368';
  }

  onDriverPhotoError(event: Event): void {
    (event.target as HTMLImageElement).style.display = 'none';
  }
}
